<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InfluencerMark from '~/components/influencer/mark.vue'
import { Sm400Black, Sm400Gray } from '~/components/typography'
import VText from '~/components/ui/VText.vue'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useTagStore } from '~/stores/tag'

import pictureBuildHelper from '~/helpers/images/picture_build'

import { Breakpoints } from '~/enums/breakpoints'

import type Tag from '~/entities/tag'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'

type Props = {
  influencer: StatsV3Influencer | Influencer
  isContest?: boolean
  selected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isContest: false,
  selected: false,
})

const { t } = useI18n()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { GET_TAGS_FROM_IDS } = useTagStore()

const titleConfig = computed(() =>
  SCREEN_WIDTH.value >= Breakpoints.sm ? 'sans/20/bold' : 'sans/16/medium',
)

const titleColor = computed(() => {
  if (props.selected) return 'gray-500'
  else return 'black'
})

const picture = computed(() =>
  pictureBuildHelper({
    target: 'profile_picture',
    size: '400_400',
    slug: props.influencer.has_profile_picture
      ? props.influencer.slug
      : undefined,
    kind: 'influencer',
  }),
)

function computeCountry() {
  return (GET_TAGS_FROM_IDS(props.influencer.tags.identity.country ?? []).map(
    (tag: Tag) => tag.name,
  ) ?? [''])[0]
}

function computeKindArray() {
  if (props.isContest)
    return t('tags.influencer_kind.springboard', 0).toString()

  return GET_TAGS_FROM_IDS([
    ...new Set(props.influencer.tags.identity.influencer_kind ?? []),
  ])
    .map((tag: Tag) => t(`tags.influencer_kind.${tag.name}`))
    .join(', ')
}

// If I had more time I would investigate the use of useState for these two
const country = computeCountry()
const kindArray = computeKindArray()
</script>

<template>
  <div
    class="influencerCardHeader tw-relative tw-flex tw-w-full"
    :class="{ isContest, selected }"
  >
    <div class="fixedWidthContent tw-flex tw-w-full tw-flex-col">
      <InfluencerMark
        class="tag"
        :influencer="influencer"
        :selected="selected"
      />
      <VText
        :cfg="titleConfig"
        :color="titleColor"
        class="name ellipsis tw-inline-block"
        :data-test-id="'influencerEntityTitle_' + influencer.id"
      >
        {{ influencer.entity }}
      </VText>
      <div class="tw-mt-1 tw-flex tw-flex-nowrap tw-items-center">
        <component
          :is="selected ? Sm400Gray : Sm400Black"
          class="kind ellipsis tw-capitalize"
          >{{ kindArray }}</component
        >
        <Sm400Gray
          v-if="$te(`tags.country.${country}`)"
          v-once
          class="tw-ml-1 tw-inline-block"
          >·</Sm400Gray
        >
        <Sm400Gray
          v-if="$te(`tags.country.${country}`)"
          v-once
          class="country tw-ellipsis tw-ml-1"
          >{{ $t(`tags.country.${country}`) }}</Sm400Gray
        >
      </div>
    </div>
    <img
      class="picture tw-ml-2 tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-full tw-shadow"
      :alt="influencer.slug"
      :class="{ selected }"
      :src="picture"
    />
  </div>
</template>

<style lang="scss" scoped>
.influencerCardHeader {
  @apply tw-h-[64px];

  &.isContest {
    @apply tw-mb-4 tw-grid tw-h-[133px] tw-flex-col-reverse tw-gap-3;

    > .picture {
      @apply tw-row-start-1 tw-m-0 tw-h-[55px] tw-w-full tw-rounded-xs tw-object-cover tw-object-center;
    }
  }
}

.tag {
  @apply tw-font-bold;
  margin-bottom: 2px;
}

.country {
  flex-shrink: 0.1;
}

.isContest .name {
  margin-bottom: 6px;
  @screen sm {
    @apply tw-mb-0;
  }
}

.fixedWidthContent {
  @apply tw-overflow-hidden;
  width: calc(100% - 64px - 8px);
}

.selected .picture {
  opacity: 0.2;
  filter: grayscale(1);
}

.kind.ellipsis {
  width: unset;
}

.selected .tag {
  @apply tw-text-gray-500;
}
</style>
